import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { Row, Col, Carousel, Modal, Spin, Button, Table, Tag } from 'antd'
import moment from 'moment'
import ApolloClientService from '../../../services/apolloclient.service'
import '../../../css/patient.scss'
import _, { set } from 'lodash'
import { useParams } from 'react-router-dom'
import {
  getOksData,
  getKoosJrData,
  getHoosJrData,
  getPainData,
  getFlexionData,
  getExtensionData,
  getFormData,
  getTracPatchData,
} from '../../../store/action/patient.action'
import Oks from './oks'
import KoosJr from './koosjr'
import HoosJr from './hoosjr'
import Pain from './pain'
import Flexion from './flexion'
import Extension from './extension'
import DeviceGraph from './devicegraph'
import TracPatchFlexionChart from './tracpatch.flexion.chart'
import TracPatchExtensionChart from './tracpatch.extension.chart'
import TracPatchStepsChart from './tracpatch.steps.chart'
import TracPatchTemperatureChart from './tracpatch.temperature.chart'
import TracPatchCadenceChart from './tracpatch.cadence.chart'
import TracPatchPainScoreChart from './tracpatch.painscore.chart'
import TracPatchLegPositionChart from './tracpatch.legposition.chart'
import TracPatchExerciseStackedChart from './tracpatch.exercise.stacked.chart'
import { RightCircleFilled, LeftCircleFilled, ReloadOutlined, DownOutlined, RightOutlined } from '@ant-design/icons'
import { constants } from '../../../constants'

const MODAL_TYPE = {
  OKS: 'OKS',
  KOOSJR: 'KOOSJR',
  PAIN: 'PAIN',
  FLEXION: 'FLEXION',
  EXTENSION: 'EXTENSION',
}

const PatientGraphs = (props) => {
  const [state, setState] = useState({
    oks: [],
    koosJr: [],
    hoosJr: [],
    pain: [],
    painLevel: [],
    flexion: [],
    extension: [],
  })
  const [tracpatch, setTracpatch] = useState({
    rom: [],
    steps: [],
    temperature: [],
    batteryAlerts: [],
  })
  const [modal, setModal] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  // Bumped by Refresh Charts so charts that fetch their own data refetch too.
  const [refreshKey, setRefreshKey] = useState(0)
  // Calibration history for this patient. Collapsed by default — the summary
  // line answers the common question ("did they, and when") and the table is
  // for the rarer one ("what happened across attempts").
  const [calibOpen, setCalibOpen] = useState(false)
  const [calibLoading, setCalibLoading] = useState(false)
  const [calibRows, setCalibRows] = useState([])
  const [calibTotal, setCalibTotal] = useState(0)
  const [calibPage, setCalibPage] = useState(1)
  const CALIB_PAGE_SIZE = 10

  var params = useParams()

  useEffect(() => {
    getData(params.id)
    // Loaded on MOUNT, not on first expand, for two reasons that both bite:
    //   1. the collapsed line reports the latest attempt, so it needs the data
    //      before anyone opens anything;
    //   2. the section's visibility gate reads calibTotal — deferring the load
    //      until expand made that circular (hidden because empty, empty
    //      because never loaded, never loaded because hidden).
    loadCalibration(1)
  }, [])

  var getData = async (id) => {
    var data = await props.getFormData(id)
    setState({
      oks: data?.oks,
      koosJr: data?.koosJr,
      hoosJr: data?.hoosJr,
      pain: data?.pain,
      painLevel: data?.painLevel,
      flexion: data?.flexion,
      extension: data?.extension,
    })
    var tpData = await props.getTracPatchData(id)
    if (tpData) {
      setTracpatch({
        rom: tpData.rom,
        steps: tpData.steps,
        temperature: tpData.temperature,
        batteryAlerts: tpData.batteryAlerts || [],
      })
    }
  }

  var refreshTracPatchData = async () => {
    setRefreshing(true)
    setRefreshKey((k) => k + 1)
    try {
      var tpData = await props.getTracPatchData(params.id)
      if (tpData) {
        setTracpatch({
          rom: tpData.rom,
          steps: tpData.steps,
          temperature: tpData.temperature,
          batteryAlerts: tpData.batteryAlerts || [],
        })
      }
    } finally {
      setRefreshing(false)
    }
  }

  // Fetched on demand rather than with the charts: most visits to this page do
  // not open it, and the history is unbounded.
  var loadCalibration = async (page) => {
    setCalibLoading(true)
    try {
      var svc = new ApolloClientService()
      var res = await svc.getTracpatchCalibrationHistory(
        params.id,
        CALIB_PAGE_SIZE,
        (page - 1) * CALIB_PAGE_SIZE
      )
      if (res && res.status === 'success') {
        setCalibRows(res.data || [])
        setCalibTotal(res.total || 0)
        setCalibPage(page)
      }
    } finally {
      setCalibLoading(false)
    }
  }

  // Pure show/hide — the data is already loaded on mount.
  var toggleCalibration = () => setCalibOpen(!calibOpen)

  // The collapsed line describes the LATEST ATTEMPT, whatever its outcome —
  // which is not the same as the last success. A patient who succeeded last
  // month and failed this morning reads "Failed", and that is the point.
  var latestAttempt = calibRows.length > 0 ? calibRows[0] : null
  // Built as spans rather than one string so the OUTCOME can carry colour while
  // the label, timestamp and leg stay plain black — the outcome is the only
  // part worth colouring, and colouring the whole line would shout.
  var CALIB_GREEN = '#237804' // dark green, readable on white at 13px
  var CALIB_RED = '#a8071a'

  var calibSummary = () => {
    var label = <span style={{ color: '#000', fontWeight: 400 }}>Calibration:</span>
    if (calibLoading && !latestAttempt) {
      return <span>{label} <span style={{ color: '#000', fontWeight: 400 }}>loading…</span></span>
    }
    if (!latestAttempt) {
      return <span>{label} <span style={{ color: '#000', fontWeight: 400 }}>Not done</span></span>
    }
    var ok = latestAttempt.outcome === 'success'
    var when = moment(latestAttempt.attemptedAt).format('M/D/YYYY h:mm A')
    var leg = latestAttempt.legSide
      ? ' (' + (latestAttempt.legSide === 'left' ? 'Left' : 'Right') + ')'
      : ''
    return (
      <span>
        {label}{' '}
        <span style={{ color: ok ? CALIB_GREEN : CALIB_RED, fontWeight: 600 }}>
          {ok ? 'Successful' : 'Failed'}
        </span>{' '}
        <span style={{ color: '#000', fontWeight: 400 }}>{when}{leg}</span>
      </span>
    )
  }

  var calibColumns = [
    {
      title: 'Status',
      dataIndex: 'outcome',
      key: 'outcome',
      width: 110,
      render: (outcome) =>
        outcome === 'success' ? (
          <Tag color='green'>Success</Tag>
        ) : (
          <Tag color='red'>Failed</Tag>
        ),
    },
    {
      title: 'Date Time',
      dataIndex: 'attemptedAt',
      key: 'attemptedAt',
      render: (d) => (d ? moment(d).format('M/D/YYYY h:mm A') : 'N/A'),
    },
    {
      title: 'Leg',
      dataIndex: 'legSide',
      key: 'legSide',
      width: 80,
      // Recorded per ATTEMPT, so a patient re-paired to the other leg shows the
      // leg each calibration actually happened on. Rows written before the
      // field existed show "—".
      render: (legSide) =>
        legSide ? (
          <span style={{ color: '#000' }}>{legSide === 'left' ? 'Left' : 'Right'}</span>
        ) : (
          <span style={{ color: 'rgba(0,0,0,0.25)' }}>—</span>
        ),
    },
    {
      title: 'Device',
      dataIndex: 'sensor',
      key: 'sensor',
      render: (sensor, row) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          {/* White = femur, Blue = tibia. The names come from the ICD step, not
              from the device name — which is why the tibia has no name to show:
              the app only ever learns the WHITE sensor's. */}
          <Tag color={sensor === 'femur' ? 'default' : 'blue'}>
            {sensor === 'femur' ? 'White' : 'Blue'}
          </Tag>
          <span style={{ color: 'rgba(0,0,0,0.65)' }}>{row.deviceName || '—'}</span>
          {row.failureCode ? (
            <span style={{ fontSize: 12, color: '#f5222d' }}>({row.failureCode})</span>
          ) : null}
        </span>
      ),
    },
  ]

  // Check if patient has a specific questionnaire assigned
  var hasQuestionnaire = (shortname) => {
    const feedbackSchedule = props.patient?.feedbackSchedule
    if (!feedbackSchedule?.lastscores?.forms) return false
    return feedbackSchedule.lastscores.forms.some(
      (form) => form.shortname === shortname
    )
  }

  // Check if patient is a knee surgery patient
  var isKneeSurgery = () => {
    const surgeryType = props.patient?.surgery?.type?.toLowerCase() || ''
    return surgeryType.includes('knee')
  }

  // Check if patient is a hip surgery patient
  var isHipSurgery = () => {
    const surgeryType = props.patient?.surgery?.type?.toLowerCase() || ''
    return surgeryType.includes('hip')
  }

  // Determine which knee score chart to show
  var getKneeScoreChart = () => {
    if (!isKneeSurgery()) return null

    // Check for KOOS JR first (newer questionnaire)
    if (hasQuestionnaire('KOOS JR')) {
      return (
        <Col flex='49%' className='chartContainer'>
          <KoosJr
            data={state.koosJr}
            onClick={handleModalOpen}
            fullscreen={!props.modal}
            showtips
          />
        </Col>
      )
    }

    // Fall back to OKS (Functional Knee)
    if (hasQuestionnaire('Functional Knee')) {
      return (
        <Col flex='49%' className='chartContainer'>
          <Oks
            data={state.oks}
            onClick={handleModalOpen}
            fullscreen={!props.modal}
            showtips
          />
        </Col>
      )
    }

    // If knee surgery but no knee questionnaire assigned, don't show any
    return null
  }

  // Determine which hip score chart to show
  var getHipScoreChart = () => {
    if (!isHipSurgery()) return null

    if (hasQuestionnaire('HOOS JR')) {
      return (
        <Col flex='49%' className='chartContainer'>
          <HoosJr
            data={state.hoosJr}
            onClick={handleModalOpen}
            fullscreen={!props.modal}
            showtips
          />
        </Col>
      )
    }

    return null
  }

  var handleModalOpen = (e) => setModal(e)

  var renderDeviceGraph = () => {
    // Build charts array dynamically based on patient's questionnaires
    const charts = []

    // Add knee score chart (OKS or KOOS JR) only for knee surgery patients
    const kneeScoreChart = getKneeScoreChart()
    if (kneeScoreChart) {
      charts.push(kneeScoreChart)
    }

    // Add hip score chart (HOOS JR) only for hip surgery patients
    const hipScoreChart = getHipScoreChart()
    if (hipScoreChart) {
      charts.push(hipScoreChart)
    }

    // Only show Pain (Pain Level) chart if patient is assigned the Pain Management questionnaire
    if (hasQuestionnaire(constants.SCORE.PAIN_LEVEL)) {
      charts.push(
        <Col flex='49%' className='chartContainer' key='pain'>
          <Pain
            data={state.painLevel}
            onClick={handleModalOpen}
            fullscreen={!props.modal}
            showtips
          />
        </Col>
      )
    }

    // Only show ROM charts (Flexion/Extension) if patient is assigned the Range of Motion questionnaire
    if (hasQuestionnaire(constants.SCORE.FLEXION)) {
      charts.push(
        <Col flex='49%' className='chartContainer' key='flexion'>
          <Flexion
            data={state.flexion}
            onClick={handleModalOpen}
            fullscreen={!props.modal}
            showtips
          />
        </Col>
      )
      charts.push(
        <Col flex='49%' className='chartContainer' key='extension'>
          <Extension
            data={state.extension}
            onClick={handleModalOpen}
            fullscreen={true}
            showtips
          />
        </Col>
      )
    }

    // Add device graphs
    if (props.patient?.deviceSchedule?.assigneddevices?.length > 0) {
      props.patient.deviceSchedule.assigneddevices.forEach((dev) => {
        if (!dev.isDeleted) {
          charts.push(
            <DeviceGraph
              key={dev._id}
              isLoading={(e) => props.isLoading(e)}
              dev={{ ...dev, patientid: props.patient._id }}
            />
          )
        }
      })
    }

    // TracPatch section — shown when the patient has TracPatch data, OR has
    // ever reported a calibration.
    //
    // ⚠️ The calibration clause is not redundant. Gating on chart data alone
    // hid the whole section for a patient who has a device but has not yet
    // produced any ROM / steps / temperature — which is exactly the patient
    // whose "Calibration: Not done" is worth seeing.
    const hasTracPatch =
      tracpatch.rom.length > 0 ||
      tracpatch.steps.length > 0 ||
      tracpatch.temperature.length > 0 ||
      calibTotal > 0

    return (
      <>
        {hasTracPatch && (
          <>
            {/* Calibration summary sits on the LEFT of the Refresh row — one
                line, expandable. It shares the row rather than taking its own
                because it is a status, not a section. */}
            <Row justify='space-between' align='middle' style={{ marginBottom: 8 }}>
              <Button
                size='small'
                type='link'
                onClick={toggleCalibration}
                style={{ paddingLeft: 0, fontWeight: 400 }}
              >
                {calibOpen ? <DownOutlined /> : <RightOutlined />} {calibSummary()}
              </Button>
              <Button
                size='small'
                icon={<ReloadOutlined />}
                loading={refreshing}
                onClick={refreshTracPatchData}
              >
                Refresh Charts
              </Button>
            </Row>
            {calibOpen && (
              <Row style={{ marginBottom: 16 }}>
                <Col span={24}>
                  <Table
                    size='small'
                    rowKey={(r, i) => r.attemptedAt + '-' + r.sensor + '-' + i}
                    loading={calibLoading}
                    columns={calibColumns}
                    dataSource={calibRows}
                    locale={{ emptyText: 'No calibration has been recorded for this patient.' }}
                    pagination={
                      calibTotal > CALIB_PAGE_SIZE
                        ? {
                            current: calibPage,
                            pageSize: CALIB_PAGE_SIZE,
                            total: calibTotal,
                            size: 'small',
                            showSizeChanger: false,
                            onChange: (pageNo) => loadCalibration(pageNo),
                          }
                        : false
                    }
                  />
                </Col>
              </Row>
            )}
            <Row style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', marginBottom: 24 }}>
              <Col flex='49%' className='chartContainer'>
                <TracPatchFlexionChart data={tracpatch.rom} />
              </Col>
              <Col flex='49%' className='chartContainer'>
                <TracPatchExtensionChart data={tracpatch.rom} />
              </Col>
              <Col flex='49%' className='chartContainer'>
                <TracPatchStepsChart data={tracpatch.steps} />
              </Col>
              <Col flex='49%' className='chartContainer'>
                <TracPatchCadenceChart
                  data={tracpatch.steps}
                  surgeryDate={props.patient && props.patient.surgery && props.patient.surgery.date}
                />
              </Col>
              <Col flex='49%' className='chartContainer'>
                <TracPatchTemperatureChart
                  data={tracpatch.temperature}
                  batteryAlerts={tracpatch.batteryAlerts}
                />
              </Col>
              <Col flex='49%' className='chartContainer'>
                <TracPatchPainScoreChart data={state.pain} />
              </Col>
            </Row>
            <Row style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', marginBottom: 24 }}>
              <Col flex='49%' className='chartContainer'>
                <TracPatchLegPositionChart
                  romData={tracpatch.rom}
                  stepsData={tracpatch.steps}
                  surgeryDate={props.patient && props.patient.surgery && props.patient.surgery.date}
                />
              </Col>
              <Col flex='49%' className='chartContainer'>
                <TracPatchExerciseStackedChart patientId={params.id} refreshKey={refreshKey} />
              </Col>
            </Row>
          </>
        )}
        <Row style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
          {charts}
        </Row>
      </>
    )
  }

  return (
    <>
      <Col flex='100%'>
        {/* <Row flex='auto'>                
                <Col flex='45%' className="chartContainer">
                    <Oks data={state.oks} onClick={handleModalOpen} fullscreen={!props.modal} showtips />
                </Col>
                <Col flex='2%'></Col>                
                <Col flex='45%' className="chartContainer" >
                    <Pain data={state.pain} onClick={handleModalOpen}  fullscreen={!props.modal} showtips />
                </Col> 
                </Row>
                <Row>&nbsp;</Row>
                <Row flex='auto'>                
                <Col flex='45%' className="chartContainer" >
                    <Flexion data={state.flexion} onClick={handleModalOpen}  fullscreen={!props.modal} showtips />
                </Col>
                <Col flex='2%'></Col>
                <Col flex='45%' className="chartContainer">                    
                    <Extension data={state.extension} onClick={handleModalOpen} fullscreen={true} showtips/>
                </Col>
                </Row>
                 <Row>&nbsp;</Row> */}
        {renderDeviceGraph()}
      </Col>
    </>
  )
}

PatientGraphs.propTypes = {
  patient: PropTypes.object,
  getReport: PropTypes.func,
}

const mapStateToProps = (state) => {
  return {
    patient: state.patient.patient,
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    getOksData: (id) => dispatch(getOksData(id)),
    getKoosJrData: (id) => dispatch(getKoosJrData(id)),
    getHoosJrData: (id) => dispatch(getHoosJrData(id)),
    getPainData: (id) => dispatch(getPainData(id)),
    getFlexionData: (id) => dispatch(getFlexionData(id)),
    getExtensionData: (id) => dispatch(getExtensionData(id)),
    getFormData: (id) => dispatch(getFormData(id)),
    getTracPatchData: (id) => dispatch(getTracPatchData(id)),
  }
}

export { MODAL_TYPE }
export default connect(mapStateToProps, mapDispatchToProps)(PatientGraphs)
